/**
 * ChatLandingPage
 * Entry point shown when user navigates to /chats with no active conversation.
 * When conversations exist but none selected, shows a placeholder.
 * When no conversations exist, shows full "Start Your Journey" UI matching Figma.
 */

import { Mic, Send } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useOutletContext } from "react-router-dom"
import { toast } from "sonner"
import peopleIcon from "@/assets/icons/people.svg"
import { useAiConversations, useCreateAiConversation } from "../../hooks/useAiChat"

export function ChatLandingPage() {
  const { t } = useTranslation("chats")
  const [input, setInput] = useState("")
  const navigate = useNavigate()
  const createConversation = useCreateAiConversation()
  const aiConversations = useAiConversations()
  const { hasConversations } = useOutletContext<{ hasConversations: boolean }>()

  // When conversations exist but none selected, show placeholder
  if (hasConversations) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <p className="text-sm text-muted-foreground">{t("chatLanding.selectChat")}</p>
      </div>
    )
  }

  const SUGGESTIONS = [
    { key: "createRitual", text: t("chatLanding.suggestions.createRitual") },
    { key: "otherTraditions", text: t("chatLanding.suggestions.otherTraditions") },
    { key: "feelConnected", text: t("chatLanding.suggestions.feelConnected") },
  ]

  const handleSend = async (message: string) => {
    if (!message.trim() || createConversation.isPending) return

    const trimmed = message.trim()

    // Reuse an existing empty conversation if present so we don't pile up stubs.
    const existingEmpty = aiConversations.data?.conversations.find(
      c => c.title === null && c.lastMessagePreview === null && c.lastMessageAt === null,
    )
    if (existingEmpty) {
      navigate(`/chats/ai/${existingEmpty.id}?firstMessage=${encodeURIComponent(trimmed)}`)
      return
    }

    try {
      const conversation = await createConversation.mutateAsync()
      navigate(`/chats/ai/${conversation.id}?firstMessage=${encodeURIComponent(trimmed)}`)
    } catch {
      toast.error(t("chatLanding.error"))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSend(input)
  }

  // No conversations: show full "Start Your Journey" landing page
  return (
    <div className="flex flex-col items-center justify-center h-full px-4">
      {/* Title */}
      <h1 className="font-montserrat font-semibold text-[30px] text-foreground uppercase opacity-90 text-center mb-3">
        {t("chatLanding.title")}
      </h1>

      {/* Subtitle */}
      <p className="font-montserrat text-[14px] leading-[25px] text-foreground opacity-90 text-center mb-8">
        {t("chatLanding.subtitle")}
      </p>

      {/* Input */}
      <form onSubmit={handleSubmit} className="relative w-full max-w-[925px] mb-6">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t("chatLanding.placeholder")}
          disabled={createConversation.isPending}
          className="w-full h-[65px] px-[29px] pr-[90px] rounded-[15px] bg-[var(--color-chat-input-bg)] border border-[var(--color-chat-input-border)] text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(242,240,235,0.3)] disabled:opacity-50"
        />
        <div className="absolute right-[19px] top-1/2 -translate-y-1/2 flex items-center gap-[15px]">
          <Mic className="size-[25px] text-muted-foreground" />
          <button
            type="submit"
            disabled={!input.trim() || createConversation.isPending}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
          >
            <Send className="size-[25px]" />
          </button>
        </div>
      </form>

      {/* Suggestion Pills - horizontal row */}
      <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
        {SUGGESTIONS.map(suggestion => (
          <button
            key={suggestion.key}
            type="button"
            onClick={() => handleSend(suggestion.text)}
            disabled={createConversation.isPending}
            className="px-[15px] py-[10px] rounded-[60px] border border-foreground text-[13px] leading-[25px] text-foreground hover:bg-[rgba(242,240,235,0.1)] transition-colors disabled:opacity-50"
          >
            {suggestion.text}
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="flex items-center gap-[10px] w-full max-w-[925px] mb-8">
        <div className="flex-1 border-t border-border" />
        <span className="text-[14px] leading-[25px] text-foreground opacity-90">
          {t("chatLanding.orConnect")}
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* Community CTAs - horizontal row */}
      <div className="flex gap-4 w-full max-w-[925px]">
        <button
          type="button"
          onClick={() => navigate("/connect")}
          className="flex-1 flex items-center gap-4 h-[95px] px-[19px] rounded-[10px] bg-[rgba(201,168,118,0.1)] border border-[rgba(201,168,118,0.1)] hover:bg-[rgba(201,168,118,0.15)] transition-colors"
        >
          <div className="size-[55px] rounded-full bg-[rgba(201,168,118,0.15)] flex items-center justify-center shrink-0">
            <img src={peopleIcon} alt="" className="size-[25px]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-[16px] text-foreground">
              {t("chatLanding.findFriends")}
            </p>
            <p className="text-[14px] text-foreground opacity-90">
              {t("chatLanding.findFriendsSubtitle")}
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate("/connect")}
          className="flex-1 flex items-center gap-4 h-[95px] px-[19px] rounded-[10px] bg-[rgba(184,184,150,0.1)] border border-[rgba(184,184,150,0.1)] hover:bg-[rgba(184,184,150,0.15)] transition-colors"
        >
          <div className="size-[55px] rounded-full bg-[rgba(184,184,150,0.15)] flex items-center justify-center shrink-0">
            <img src={peopleIcon} alt="" className="size-[25px]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-[16px] text-foreground">
              {t("chatLanding.findExperts")}
            </p>
            <p className="text-[14px] text-foreground opacity-90">
              {t("chatLanding.findExpertsSubtitle")}
            </p>
          </div>
        </button>
      </div>
    </div>
  )
}
